import {
  CorrectIcon,
  PendingIcon,
  TerminatedIcon,
  UnassignedIcon,
  WrongIcon,
} from "../assets/icons";

export const STANDARD_USER_ROLE_ID = 5;
export const PUBLIC_DEFAULT_LICENSE_ID = 1;
export const DUPONT_DEFAULT_LICENSE_ID = 9;

export const HIDDEN_LICENSE_IDS = [
  PUBLIC_DEFAULT_LICENSE_ID,
  DUPONT_DEFAULT_LICENSE_ID,
];

// No need to consider the PUBLIC license here, even though it appears under AI Recommendations/Organo.
// It belongs to the default Public license, which does not expire.
export const LICENSE_PERMISSION_IDS = {
  AI_RECOMMENDATIONS: 3,
  USE_PERMEABILITY: 5,
  MLD: 8,
  LI: 9,
  VEOLIA: 10,
  RESTRICTED: 11,
  SWBR: 12,
  SR90_TEAM: 13,
};

export const LICENSE_TAB_IDS = {
  AI_RECOMMENDATIONS: "roa_ai_recommendations",
  USE_PERMEABILITY: "roa_use_permeability",
  SPECIAL_RO_ELEMENTS: "roa_special_ro_elements",
};

export const LICENSE_TAB_LABEL_KEY_BY_ID = {
  [LICENSE_TAB_IDS.AI_RECOMMENDATIONS]:
    "licensedFeaturesDocumentation.aiRecommendationsTitle",
  [LICENSE_TAB_IDS.USE_PERMEABILITY]:
    "licensedFeaturesDocumentation.usePermeabilityTitle",
  [LICENSE_TAB_IDS.SPECIAL_RO_ELEMENTS]:
    "licensedFeaturesDocumentation.specialRoElementsTitle",
};

export const INTRODUCTION_TAB = {
  id: "introduction_page",
  labelKey: "licensedFeaturesDocumentation.introTitle",
};

export const LICENSE_TAB_ID_TO_PERMISSION_IDS_MAP = {
  [LICENSE_TAB_IDS.AI_RECOMMENDATIONS]: [
    LICENSE_PERMISSION_IDS.AI_RECOMMENDATIONS,
  ],
  [LICENSE_TAB_IDS.USE_PERMEABILITY]: [LICENSE_PERMISSION_IDS.USE_PERMEABILITY],
  [LICENSE_TAB_IDS.SPECIAL_RO_ELEMENTS]: [
    LICENSE_PERMISSION_IDS.MLD,
    LICENSE_PERMISSION_IDS.LI,
    LICENSE_PERMISSION_IDS.VEOLIA,
    LICENSE_PERMISSION_IDS.RESTRICTED,
    LICENSE_PERMISSION_IDS.SWBR,
    LICENSE_PERMISSION_IDS.SR90_TEAM,
  ],
};

export const LICENSE_FILTER_ALL = 1;

export const MS_IN_A_DAY = 24 * 60 * 60 * 1000;
export const LICENSE_EXPIRING_SOON_DAYS = 7;

export const LICENSE_STATUS = {
  expiringSoon: "ExpiringSoon",
  expired: "Expired",
  active: "Active",
  terminated: "Terminated",
  unassigned: "Unassigned",
};

export const LICENSE_SORT_DIRECTION = {
  ASC: "ASC",
  DESC: "DESC",
};

export const LICENSE_TYPES = {
  public: "Public",
  duPont: "DuPont",
  roSpecific: "RO Specific",
};

export const DEFAULT_LICENSE_PAYLOAD = {
  licenses: [],
  rolenames: [],
  companyName: "",
  searchstr: "",
  pageNumber: 1,
  pageSize: 20,
  sortBy: "",
  sortDirection: "",
  licenseStatus: 1,
  fromDate: null,
  toDate: null,
};

export const LICENSE_FILTER_OPTIONS = [
  {
    id: 1,
    label: "licenseManagement.filterAll",
    icon: null,
    dataTestId: "all-filter",
  },
  {
    id: 2,
    label: "licenseManagement.filterActive",
    icon: <CorrectIcon />,
    dataTestId: "active-filter",
  },
  {
    id: 3,
    label: "licenseManagement.filterExpiringSoon",
    icon: <PendingIcon />,
    dataTestId: "expiring-soon-filter",
  },
  {
    id: 4,
    label: "licenseManagement.filterExpired",
    icon: <WrongIcon />,
    dataTestId: "expired-filter",
  },
  {
    id: 5,
    label: "licenseManagement.filterTerminated",
    icon: <TerminatedIcon />,
    dataTestId: "terminated-filter",
  },
  {
    id: 6,
    label: "licenseManagement.filterUnassigned",
    icon: <UnassignedIcon />,
    dataTestId: "unassigned-filter",
  },
];

export const EXPORT_PAYLOAD = {
  licenses: [],
  rolenames: [],
  companyName: "",
  searchstr: "",
  pageNumber: 0,
  pageSize: 0,
  sortBy: "",
  sortDirection: "",
  licenseStatus: 1,
  fromDate: null,
  toDate: null,
};

export const LICENSE_STATUS_FILTER = [
  {
    id: "active",
    icon: <CorrectIcon />,
    label: "licenseManagement.filterActive",
    name: "Active",
  },
  {
    id: "expiringSoon",
    icon: <PendingIcon />,
    label: "licenseManagement.filterExpiringSoon",
    name: "Expiring Soon",
  },
  {
    id: "expired",
    icon: <WrongIcon />,
    label: "licenseManagement.filterExpired",
    name: "Expired",
  },
  {
    id: "terminated",
    icon: <TerminatedIcon />,
    label: "licenseManagement.filterTerminated",
    name: "Terminated",
  },
];

export const DATE_RANGE_ACTIONS = {
  apply: "apply",
  clear: "clear",
};

export const licenseActionMenuKeys = {
  VIEW_USER: "view-user",
  ASSIGN_LICENSE: "assign-license",
  RENEW_LICENSE: "renew-license",
  TERMINATE_LICENSE: "terminate-license",
};

export const LICENSE_VIEW_USER_MODAL_KEYS = {
  assignModal: "assign",
  renewModal: "renew",
  terminateModal: "terminate",
};

/**
 * Mapping of license status property keys to their display types
 */
export const STATUS_KEY_TO_TYPE_MAP = {
  isActive: "active",
  isExpiringSoon: "expiringSoon",
  isExpired: "expired",
  isTerminated: "terminated",
  isUnassigned: "unassigned",
};

export const STATUS_FILTER_ICONS = {
  active: <CorrectIcon />,
  expiringSoon: <PendingIcon />,
  expired: <WrongIcon />,
  terminated: <TerminatedIcon />,
};

export const NO_FILTERS_APPLIED = {
  licenses: [],
  rolenames: [],
  companyName: "",
  searchstr: "",
  fromDate: null,
  toDate: null,
};

export const LICENSE_STATUS_ICONS = [
  { icon: <CorrectIcon />, label: "licenseManagement.filterActive" },
  {
    icon: <PendingIcon />,
    label: "licenseManagement.filterExpiringSoon",
  },
  { icon: <WrongIcon />, label: "licenseManagement.filterExpired" },
  {
    icon: <TerminatedIcon />,
    label: "licenseManagement.filterTerminated",
  },
  {
    icon: <UnassignedIcon />,
    label: "licenseManagement.filterUnassigned",
  },
];
